import { Camera, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { SignaturePad } from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Row = ({ label, children, hint }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

/** The KYC/AML form fields — shared by the public customer form and the staff counter capture. */
export const KycFields = ({
  form,
  set,
  files,
  onRemoveFile,
  upload,
  uploading,
  signature,
  setSignature,
  accepted,
  setAccepted,
  privacyOk,
  setPrivacyOk,
  branch,
}) => (
  <>
    <Row label="Name as per passport *">
      <Input className="h-10 rounded-none" value={form.name_as_per_passport}
             onChange={set("name_as_per_passport")} data-testid="kyc-passport-name" />
    </Row>
    <Row label="Nationality">
      <Input className="h-10 rounded-none" value={form.nationality}
             onChange={set("nationality")} data-testid="kyc-nationality" />
    </Row>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Row label="Driver licence no">
        <Input className="h-10 rounded-none" value={form.licence_number}
               onChange={set("licence_number")} data-testid="kyc-licence-number" />
      </Row>
      <Row label="State">
        <Input className="h-10 rounded-none" value={form.licence_state}
               onChange={set("licence_state")} data-testid="kyc-licence-state" />
      </Row>
      <Row label="Licence expiry">
        <Input type="date" className="h-10 rounded-none" value={form.licence_expiry}
               onChange={set("licence_expiry")} data-testid="kyc-licence-expiry" />
      </Row>
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Row label="Passport number">
        <Input className="h-10 rounded-none" value={form.passport_number}
               onChange={set("passport_number")} data-testid="kyc-passport-number" />
      </Row>
      <Row label="Issue date">
        <Input type="date" className="h-10 rounded-none" value={form.passport_issue}
               onChange={set("passport_issue")} data-testid="kyc-passport-issue" />
      </Row>
      <Row label="Passport expiry">
        <Input type="date" className="h-10 rounded-none" value={form.passport_expiry}
               onChange={set("passport_expiry")} data-testid="kyc-passport-expiry" />
      </Row>
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Row label="Company name (if any)">
        <Input className="h-10 rounded-none" value={form.company_name}
               onChange={set("company_name")} data-testid="kyc-company" />
      </Row>
      <Row label="ABN">
        <Input className="h-10 rounded-none" value={form.abn} onChange={set("abn")}
               data-testid="kyc-abn" />
      </Row>
      <Row label="ACN">
        <Input className="h-10 rounded-none" value={form.acn} onChange={set("acn")}
               data-testid="kyc-acn" />
      </Row>
    </div>
    <Row label="Address">
      <Input className="h-10 rounded-none" value={form.address} onChange={set("address")}
             data-testid="kyc-address" />
    </Row>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Row label="Email" hint="We'll send a copy of this form">
        <Input className="h-10 rounded-none" value={form.email} onChange={set("email")}
               data-testid="kyc-email" />
      </Row>
      <Row label="WhatsApp mobile no *">
        <Input className="h-10 rounded-none" value={form.whatsapp_mobile}
               onChange={set("whatsapp_mobile")} data-testid="kyc-whatsapp" />
      </Row>
    </div>

    <div className="border border-border p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Photos of TWO forms of photo ID *
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        For example a driver licence and a passport. Attach a photo of each.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 border border-border px-4 py-2 text-sm">
          <Camera className="h-4 w-4" /> Take photo
          <input type="file" accept="image/*" capture="environment" className="hidden"
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   e.target.value = "";
                   if (f) upload(f);
                 }}
                 data-testid="kyc-camera-input" />
        </label>
        <label className="inline-flex cursor-pointer items-center gap-2 border border-border px-4 py-2 text-sm">
          <Upload className="h-4 w-4" /> Attach file
          <input type="file" accept="image/*,application/pdf" className="hidden"
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   e.target.value = "";
                   if (f) upload(f);
                 }}
                 data-testid="kyc-file-input" />
        </label>
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <p className="mt-2 text-xs font-semibold text-[#8A6A22]" data-testid="kyc-two-ids-note">
        Two forms of photo ID are required — for example a driver licence and a passport.
        Attach a photo of each.
      </p>
      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={f.file_id} className="flex items-center gap-2 border border-border px-3 py-2 text-xs"
                 data-testid={`kyc-attachment-${i}`}>
              <FileText className="h-3.5 w-3.5" />
              {(f.size / 1024).toFixed(0)} KB
              <button type="button" onClick={() => onRemoveFile(i)}
                      data-testid={`kyc-attachment-remove-${i}`} aria-label="Remove">
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    <div className="border border-border p-4 text-sm">
      <p className="font-bold uppercase">Declaration by customer</p>
      <p className="mt-2 text-muted-foreground">
        I/we hereby declare that details furnished above are true and correct to the best of
        my/our knowledge and I/we further undertake to notify Dhunna Jewellers promptly
        whenever there are any changes to this information. In case any of the above mentioned
        information is found to be false or untrue or misleading or misrepresenting, I/we
        am/are aware that I/we may be held liable for it.
      </p>
      <PrivacyNotice
        branch={branch}
        accepted={privacyOk}
        onChange={setPrivacyOk}
        testid="kyc-privacy"
      />
      <label className="mt-3 flex items-start gap-2">
        <input type="checkbox" checked={accepted} className="mt-1"
               onChange={(e) => setAccepted(e.target.checked)}
               data-testid="kyc-declaration-checkbox" />
        I accept the declaration above.
      </label>
    </div>

    <Row label="Customer name *">
      <Input className="h-10 rounded-none" value={form.customer_name}
             onChange={set("customer_name")} data-testid="kyc-customer-name" />
    </Row>

    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Signature
      </Label>
      <div className="mt-2">
        {signature ? (
          <div className="flex items-center gap-3">
            <img src={signature} alt="Signature" className="h-16 border border-border"
                 data-testid="kyc-signature-preview" />
            <Button variant="outline" className="rounded-none" onClick={() => setSignature("")}
                    data-testid="kyc-signature-clear">
              Sign again
            </Button>
          </div>
        ) : (
          <SignaturePad onSave={(img) => setSignature(img)} testidPrefix="kyc-signature" />
        )}
      </div>
    </div>
  </>
);
